// OpenAI API integration for artwork generation
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

/**
 * Generate artistic interpretation of an activity using OpenAI
 * @param {Object} activity - Strava activity data
 * @returns {Promise<Object>} - Generated artwork data
 */
export const generateArtworkFromActivity = async (activity) => {
  try {
    // Format activity data for the prompt
    const activityData = {
      name: activity.name,
      type: activity.type,
      distance: (activity.distance / 1000).toFixed(2),
      duration: formatTime(activity.moving_time),
      pace: formatPace(activity.average_speed),
      elevation: activity.total_elevation_gain,
      date: new Date(activity.start_date).toLocaleDateString(),
      effort: activity.average_heartrate ? `Heart rate: ${activity.average_heartrate}bpm` : 'Moderate effort'
    };

    // Use the OpenAI API with the provided key
    if (OPENAI_API_KEY && OPENAI_API_KEY !== 'your_openai_api_key') {
      return await callOpenAIAPI(activityData);
    } else {
      // Fallback to mock implementation if no key
      return await mockOpenAIResponse(activityData);
    }
  } catch (error) {
    console.error('Error generating artwork:', error);
    throw new Error('Failed to generate artwork. Please try again.');
  }
};

/**
 * Call the OpenAI API to generate artwork
 * @param {Object} activityData - Formatted activity data
 * @returns {Promise<Object>} - Generated artwork data
 */
const callOpenAIAPI = async (activityData) => {
  const prompt = `
Create an artistic interpretation of a ${activityData.type} activity with the following data:
- Name: "${activityData.name}"
- Distance: ${activityData.distance} km
- Duration: ${activityData.duration}
- Average Pace: ${activityData.pace}
- Elevation Gain: ${activityData.elevation}m
- Date: ${activityData.date}
- ${activityData.effort}

Create:
1. An artwork theme based on the activity data
2. A painting concept (style and description)
3. A poetic title (10-30 words)
4. A short description poem (20-50 words)
5. An image generation prompt that would create a visual representation of this activity

Make it emotional, inspiring, and connected to the effort shown in the activity.

Format the response as JSON with the following structure:
{
  "artwork": {
    "theme": "...",
    "style": "...",
    "poeticInfluence": "...",
    "palette": "..."
  },
  "title": "...",
  "poem": "...",
  "imagePrompt": "..."
}
`;

  try {
    console.log('Calling OpenAI API for artwork generation...');
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-4',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 800
      })
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('OpenAI API error:', error);
      throw new Error(error.error?.message || 'API request failed');
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    
    console.log('OpenAI response received successfully');
    
    // Parse the JSON response
    try {
      return JSON.parse(content);
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', content);
      // Fallback to mock response if parsing fails
      return mockOpenAIResponse(activityData);
    }
  } catch (error) {
    console.error('OpenAI API error:', error);
    // Fallback to mock response
    return mockOpenAIResponse(activityData);
  }
};

/**
 * Generate a mock response for when the API is not available
 * @param {Object} activityData - Formatted activity data
 * @returns {Promise<Object>} - Mock generated artwork data
 */
const mockOpenAIResponse = async (activityData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  // Generate artistic styles based on activity metrics
  const styles = [
    'Abstract Expressionism',
    'Impressionist',
    'Digital Minimalism',
    'Geometric',
    'Watercolor',
    'Urban Sketch',
    'Neon Noir'
  ];

  const poets = [
    'Robert Frost',
    'Maya Angelou',
    'Pablo Neruda',
    'Mary Oliver',
    'Langston Hughes',
    'Emily Dickinson',
    'Rumi'
  ];

  // Activity intensity affects color palette
  const getPalette = () => {
    const speed = activityData.type === 'Run' ? 
      parseFloat(activityData.pace) : 
      parseFloat(activityData.distance) / (parseFloat(activityData.duration.split(':')[0]) * 60);
    
    if (speed > 4) return 'fiery reds and deep crimsons';
    if (speed > 3.5) return 'vibrant oranges and golden yellows';
    if (speed > 3) return 'rich blues and purples';
    return 'serene greens and cool blues';
  };

  // Distance affects the complexity
  const getComplexity = () => {
    const distance = parseFloat(activityData.distance);
    if (distance > 15) return 'intricate and detailed';
    if (distance > 10) return 'balanced with moderate detail';
    if (distance > 5) return 'clean and focused';
    return 'minimal and essential';
  };

  // Elevation affects the texture
  const getTexture = () => {
    const elevation = parseFloat(activityData.elevation);
    if (elevation > 300) return 'rough and textured';
    if (elevation > 150) return 'varied with subtle textures';
    if (elevation > 50) return 'smooth with occasional texture';
    return 'sleek and polished';
  };

  return {
    artwork: {
      theme: `A ${getComplexity()} journey through ${getPalette()} landscapes, ${getTexture()} in execution.`,
      style: styles[Math.floor(Math.random() * styles.length)],
      poeticInfluence: poets[Math.floor(Math.random() * poets.length)],
      palette: getPalette()
    },
    title: generatePoetryTitle(activityData),
    poem: generatePoem(activityData),
    imagePrompt: `${getComplexity()} artwork in style of ${styles[Math.floor(Math.random() * styles.length)]} with ${getPalette()} color palette, depicting a runner's journey through a landscape that feels ${parseFloat(activityData.elevation) > 100 ? 'challenging and elevated' : 'flowing and rhythmic'}. ${getTexture()} brushstrokes.`
  };
};

/**
 * Generate an image using DALL-E or similar service
 * @param {string} prompt - Image generation prompt
 * @returns {Promise<Object>} - Generated image data
 */
export const generateImage = async (prompt) => {
  try {
    // Use the OpenAI API for DALL-E with the provided key
    if (OPENAI_API_KEY && OPENAI_API_KEY !== 'your_openai_api_key') {
      return await generateDallEImage(prompt);
    } else {
      // Otherwise use the mock implementation
      return await mockImageGeneration(prompt);
    }
  } catch (error) {
    console.error('Error generating image:', error);
    return mockImageGeneration(prompt);
  }
};

/**
 * Generate an image using DALL-E API
 * @param {string} prompt - Image generation prompt
 * @returns {Promise<Object>} - Generated image data
 */
const generateDallEImage = async (prompt) => {
  try {
    console.log('Calling DALL-E API for image generation...');
    
    const response = await fetch('https://api.openai.com/v1/images/generations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'dall-e-3',
        prompt: prompt,
        n: 1,
        size: '1024x1024'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('DALL-E API error:', error);
      throw new Error(error.error?.message || 'API request failed');
    }

    const data = await response.json();
    console.log('DALL-E image generated successfully');
    
    return {
      url: data.data[0].url,
      prompt: prompt
    };
  } catch (error) {
    console.error('DALL-E API error:', error);
    throw error;
  }
};

/**
 * Mock image generation for when the API is not available
 * @param {string} prompt - Image generation prompt
 * @returns {Promise<Object>} - Mock generated image data
 */
const mockImageGeneration = async (prompt) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 2000));

  // Return a placeholder image based on the prompt keywords
  const images = [
    'https://images.unsplash.com/photo-1528543606781-2f6e6857f318?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1501868984184-76121ed6a6e2?w=600&h=600&fit=crop'
  ];

  return {
    url: images[Math.floor(Math.random() * images.length)],
    prompt: prompt
  };
};

// Helper function to generate a poetic title
const generatePoetryTitle = (activityData) => {
  const titles = [
    `Where ${activityData.type === 'Run' ? 'Feet' : 'Wheels'} Meet Horizon's Edge`,
    `The ${parseFloat(activityData.distance)}km Symphony of Motion`,
    `Pulse of the ${activityData.type === 'Run' ? 'Runner' : 'Rider'}, Song of the Road`,
    `Chasing Dawn Through ${parseFloat(activityData.elevation) > 100 ? 'Mountains' : 'Valleys'}`,
    `The Rhythm of ${activityData.pace} Pace`,
    `Solitude's Canvas: A ${activityData.type}'s Journey`,
    `Breathing in Miles, Exhaling Moments`,
    `${activityData.name}: A Portrait in Motion`
  ];

  return titles[Math.floor(Math.random() * titles.length)];
};

// Helper function to generate a poem
const generatePoem = (activityData) => {
  const poems = [
    `Each stride a brushstroke on time's canvas. The road unfolds its secrets only to those who dare to move forward, heart pulsing with the rhythm of determination.`,
    `Between breath and horizon, we find ourselves. ${activityData.elevation}m of elevation, each meter a verse in the poem of effort and release.`,
    `The body speaks in rhythm, the mind in color. Together they painted ${activityData.distance} kilometers of existence against the backdrop of fleeting time.`,
    `Not all who wander are lost; some are simply creating art with their ${activityData.type === 'Run' ? 'feet' : 'wheels'}. A ${activityData.duration} masterpiece of motion.`,
    `The landscape breathes with you, rises with your effort. Each kilometer a stanza in the epic of your becoming.`
  ];

  return poems[Math.floor(Math.random() * poems.length)];
};

// Format time helper
const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format pace helper
const formatPace = (metersPerSecond) => {
  const minutesPerKm = 1000 / (metersPerSecond * 60);
  const minutes = Math.floor(minutesPerKm);
  const seconds = Math.floor((minutesPerKm - minutes) * 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
};